import { useMemo } from 'react';

import { SmallLinkIcon } from '@/assets/icons';
import { ROOT_FOLDER_PATH } from '@/common/constants';
import { DataRoomClientFile } from '@/common/types';
import { Folder } from '@/contexts/overview/dataroom/DataRoomContext';
import { getFileFolderPaths } from '@/pages/overview/dataroom/content/common/folderUtils';

// Tooltip configuration constants
const TOOLTIP_CONFIG = {
  BASE_HEIGHT: 32,
  ITEM_HEIGHT: 16,
  PADDING: 16,
  MIN_WIDTH: 200,
  MAX_WIDTH: 320,
  CHARS_PER_PX: 7,
  ICON_GAP: 8,
} as const;

interface LinkedFileIndicatorProps {
  file: DataRoomClientFile;
  foldersByPaths: Map<string, Folder[]>;
  containerStyle?: string;
}

const getFolderDisplayName = (path: string, foldersByPaths: Map<string, Folder[]>): string => {
  // Show "Document Set" for root folder
  if (path === ROOT_FOLDER_PATH) {
    return 'Document Set';
  }

  const folders = foldersByPaths.get(path);
  if (!folders || folders.length === 0) {
    const pathSegments = path.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Handle edge cases for root folder
    if (!lastSegment || lastSegment === 'root') {
      return 'Document Set';
    }
    return lastSegment;
  }

  const folder = folders[0];
  const displayName = folder.displayName ?? folder.name ?? 'Unknown Folder';

  // Additional check in case folder name/displayName is root-related
  if (folder.key === 'root' || displayName === 'root' || displayName === '/') {
    return 'Document Set';
  }

  return displayName;
};

export const LinkedFileIndicator = ({
  file,
  foldersByPaths,
  containerStyle = '',
}: LinkedFileIndicatorProps) => {
  const folderPaths = getFileFolderPaths(file, foldersByPaths);
  const folderCount = folderPaths.length;

  // Memoize folder names calculation
  const folderNames = useMemo(() => {
    return folderPaths
      .map((path) => getFolderDisplayName(path, foldersByPaths))
      .filter(Boolean)
      .sort();
  }, [folderPaths, foldersByPaths]);

  // Memoize tooltip dimensions and positioning
  const tooltipStyle = useMemo(() => {
    const totalHeight =
      TOOLTIP_CONFIG.BASE_HEIGHT +
      folderNames.length * TOOLTIP_CONFIG.ITEM_HEIGHT +
      TOOLTIP_CONFIG.PADDING;
    const topPosition = -(totalHeight + TOOLTIP_CONFIG.ICON_GAP);

    const maxNameLength = Math.max(...folderNames.map((name) => name.length));
    const dynamicWidth = Math.min(
      Math.max(TOOLTIP_CONFIG.MIN_WIDTH, maxNameLength * TOOLTIP_CONFIG.CHARS_PER_PX + 40),
      TOOLTIP_CONFIG.MAX_WIDTH,
    );

    return {
      top: `${topPosition}px`,
      width: `${dynamicWidth}px`,
      minHeight: `${totalHeight}px`,
    };
  }, [folderNames]);

  // Only show indicator if file exists in multiple folders
  if (folderCount <= 1) {
    return null;
  }

  return (
    <div className={`group relative shrink-0 ${containerStyle}`}>
      <img
        src={SmallLinkIcon}
        alt="linked-file-icon"
        className="translate-y-[2px] cursor-pointer group-hover:filter-hover-filter"
      />
      <div
        className="absolute left-1/2 z-[100] hidden -translate-x-1/2 rounded-[8px] border border-[#D9D9D9] bg-white p-2 text-center text-[12px] font-medium text-black shadow-lg after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-8 after:border-transparent after:border-t-white group-hover:block"
        style={tooltipStyle}
      >
        <div className="mb-1 font-semibold">
          File exists in {folderCount} folder{folderCount !== 1 ? 's' : ''}:
        </div>
        <div className="space-y-0.5 text-left">
          {folderNames.map((name, index) => (
            <div key={index} className="break-words leading-4">
              • {name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
